"use client"

import { CheckIcon, XMarkIcon } from "@heroicons/react/16/solid"
import clsx from "clsx"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"
import Spinner from "../Spinner"

export type StepSize = "sm" | "md"

export function ProgressStep({
  label,
  size,

  status,

  isLast,
  isCurrent,
  isPast,
  isFuture,

  collapsed,
}: {
  label: string
  size: StepSize

  status: "success" | "error" | "pending"

  isLast: boolean
  isCurrent: boolean
  isPast: boolean
  isFuture: boolean

  collapsed?: boolean
}) {
  const iconClasses = clsx("text-white", {
    "size-5": size === "md" && !collapsed,
    "size-7": size === "md" && collapsed,
    "size-4": size === "sm",
  })

  return (
    <div
      className={clsx("relative flex items-center", {
        "pb-7": size === "md" && !isLast,
        "pb-4": size === "sm" && !isLast,
      })}
    >
      {/* Line between steps */}
      {!isLast && (
        <div
          className={clsx("absolute top-1 h-full transition-colors", {
            "-translate-x-0.5 left-4 w-1": size === "md",
            "-translate-x-px left-2.5 w-0.5": size === "sm",

            "bg-red-500": status === "error",
            "bg-green-600 dark:bg-green-700": status === "success",
            "bg-sky-400 dark:bg-sky-500":
              status === "pending" && !isCurrent && !isFuture,
            "bg-gray-200 dark:bg-gray-600":
              status === "pending" && (isCurrent || isFuture),
          })}
        />
      )}

      {/* Icon */}
      <motion.div
        className={clsx(
          "relative flex shrink-0 items-center justify-center rounded-full transition-colors",
          {
            "size-5": size === "sm",

            "bg-red-500": status === "error",
            "bg-green-600 dark:bg-green-700": status === "success",
            "bg-sky-400 dark:bg-sky-500": status === "pending" && !isFuture,
            "bg-gray-200 dark:bg-gray-600": status === "pending" && isFuture,
          }
        )}
        animate={{
          width: collapsed ? 44 : size === "md" ? 32 : undefined,
          height: collapsed ? 44 : size === "md" ? 32 : undefined,
        }}
        transition={{
          type: "spring",
          duration: 0.2,
          bounce: 0.15,
        }}
      >
        {isPast ? (
          <CheckIcon className={iconClasses} />
        ) : isCurrent && status === "pending" ? (
          <Spinner size={size} className="text-white" />
        ) : isCurrent && status === "success" ? (
          <CheckIcon className={iconClasses} />
        ) : isCurrent && status === "error" ? (
          <XMarkIcon className={iconClasses} />
        ) : (
          <div
            className={clsx("absolute rounded-full bg-white dark:bg-gray-800", {
              "inset-0.5": size === "sm",
              "inset-1": size === "md",
            })}
          />
        )}
      </motion.div>

      {/* Label */}
      <div
        className={clsx(
          "flex items-center justify-center font-semibold leading-none! transition-all",
          {
            "ml-3": size === "md",
            "text-base": size === "md" && collapsed,
            "text-sm": size === "md" && !collapsed,
            "ml-2 text-sm": size === "sm",

            "text-red-500": status === "error",
            "text-green-600": status === "success",
            "text-sky-400 dark:text-sky-500": status === "pending" && !isFuture,
            "text-gray-400": status === "pending" && isFuture,
          }
        )}
      >
        {label}
      </div>
    </div>
  )
}

type ProgressStepsProps<TStage extends string> = {
  stages: readonly TStage[]
  stageLabels: Record<TStage, string>
  displayStage: TStage
  displayIndex: number
  isError: boolean
  isSuccess: boolean
  size: StepSize
}

export function ProgressSteps<TStage extends string>({
  stages,
  stageLabels,
  displayStage,
  displayIndex,
  isError,
  isSuccess,
  size,
}: ProgressStepsProps<TStage>) {
  const shouldCollapse = isSuccess && !isError && size === "md"
  const [collapsed, setCollapsed] = useState(shouldCollapse)

  useEffect(() => {
    if (!shouldCollapse) {
      setCollapsed(false)
      return
    }
    const timer = setTimeout(() => setCollapsed(true), 2000)
    return () => clearTimeout(timer)
  }, [shouldCollapse])

  return (
    <ol>
      <AnimatePresence initial={false}>
        {stages.map((stage, i) => {
          const isLastStep = i === stages.length - 1

          if (collapsed && !isLastStep) return null

          return (
            <motion.li
              key={stage}
              initial={false}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{
                type: "spring",
                duration: 0.2,
                bounce: 0.15,
              }}
              className="overflow-hidden"
            >
              <ProgressStep
                size={size}
                label={
                  stage === "complete" && isError
                    ? "Failed"
                    : stageLabels[stage]
                }
                status={isError ? "error" : isSuccess ? "success" : "pending"}
                isLast={isLastStep}
                isCurrent={displayStage === stage}
                isPast={displayIndex > i}
                isFuture={displayIndex < i}
                collapsed={collapsed && isLastStep}
              />
            </motion.li>
          )
        })}
      </AnimatePresence>
    </ol>
  )
}
